// ============================================================================
// CRS — Labour Market Integration factor (in force from November 3, 2026).
// ============================================================================
// Source (IRCC, all pages "Date modified 2026-10-16"):
//   - 2026 changes to points for Canadian work experience and job offers
//     https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/changes.html
//   - Express Entry high-wage occupations list (based on 2023 to 2025 data)
//     https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/check-score/high-wage.html#list
//
// The rules, as IRCC states them:
//   - Option A, Canadian work experience: the work-length points (the same table
//     as the old Canadian work experience factor, max 70 with a spouse / 80
//     without) PLUS high-wage points for at least 12 months of qualifying Canadian
//     work in high-wage occupations: 70 (>= 2x the average median wage), else 40
//     (>= 1.5x), else 20 (>= 1.3x).
//   - Option B, a job offer valid under Express Entry in a high-wage occupation:
//     100 (>= 2x), 70 (>= 1.5x), 50 (>= 1.3x); not high-wage = 0. No LMIA needed.
//   - "Only ... Canadian work experience or an eligible job offer, not both" —
//     the higher of the two counts. Max 150 without a spouse, 140 with one.
//   - Skills transferability is unchanged and still uses plain Canadian work years.
//
// The tier lists below were copied from the official list and checked against
// its French version (identical, 186 codes). When IRCC republishes the list,
// replace them and bump LIST_DATE — never edit a code by hand.
// ============================================================================

export const LMI_EFFECTIVE_DATE = '2026-11-03';
export const LIST_DATE = '2026-10-16';
export const LIST_DATA_YEARS = '2023 to 2025';
export const CHANGES_URL = 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/changes.html';
export const HIGH_WAGE_LIST_URL = 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/check-score/high-wage.html#list';

const TIER_2X = '00011 00012 00013 10010 10011 10012 10020 10021 20010 20011 20012 21330 21332 31100 31101 31102 31103 31302 31303 40010 40011 40012 40019 40040 40041 41100 41101 41200 60010 80010 90011';
const TIER_1_5X = '00014 00015 10019 10022 10029 10030 11103 11201 21100 21102 21103 21109 21111 21120 21202 21210 21211 21220 21221 21222 21223 21230 21231 21232 21300 21301 21310 21311 21320 21321 21322 21331 21390 21399 22231 22312 30010 31110 31112 31120 31200 31202 31203 31300 31301 32111 40020 40021 40030 40042 41210 41220 41221 41310 41311 41320 41401 41402 41404 41407 42100 42101 50010 50011 51100 70010 70012 70020 70021 72011 72012 72023 72103 72202 72203 72301 72406 72600 72601 72604 73310 82020 82021 83101 90010 92011 92100 93100 93101';
const TIER_1_3X = '00010 11100 11101 11102 11109 11200 12201 12203 21101 21110 21112 21200 21201 21203 21234 22214 22232 22233 22310 22313 31121 32102 32103 32120 32121 32122 41300 41400 41403 41405 41409 43201 43203 50012 51101 51102 51111 51112 51120 52112 60020 60031 60040 72010 72013 72014 72020 72021 72102 72105 72201 72302 72320 72321 72401 72403 72404 72500 72602 72603 73311 82010 83100 83120 92010 93102';

const TIER_BY_NOC = new Map([
  ...TIER_1_3X.split(' ').map(code => [code, '1.3x']),
  ...TIER_1_5X.split(' ').map(code => [code, '1.5x']),
  ...TIER_2X.split(' ').map(code => [code, '2x']),
]);

const HIGH_WAGE_WORK_POINTS = { '2x': 70, '1.5x': 40, '1.3x': 20 };
const JOB_OFFER_POINTS = { '2x': 100, '1.5x': 70, '1.3x': 50 };

/** '2x' | '1.5x' | '1.3x' for an occupation on IRCC's high-wage list, else null. */
export function highWageTier(noc) {
  return TIER_BY_NOC.get(String(noc || '').trim()) || null;
}

/** True once the new factor applies (Canada's Eastern time, where IRCC dates its changes). */
export function isLabourMarketFactorInForce(now = new Date()) {
  return now.getTime() >= new Date(`${LMI_EFFECTIVE_DATE}T00:00:00-05:00`).getTime();
}

export function labourMarketMax(hasSpouse) {
  return hasSpouse ? 140 : 150;
}

/**
 * @param workLengthPoints  points from the Canadian work experience table (0-70/80)
 * @param highWageWorkNoc   occupation with at least 12 months of the candidate's Canadian work, or ''
 * @param jobOfferNoc       occupation of a job offer valid under Express Entry, or ''
 */
export function getLabourMarketPoints({ workLengthPoints, highWageWorkNoc, jobOfferNoc }) {
  const workTier = highWageTier(highWageWorkNoc);
  const highWage = workTier && workLengthPoints > 0 ? HIGH_WAGE_WORK_POINTS[workTier] : 0;
  const offerTier = highWageTier(jobOfferNoc);
  const jobOffer = offerTier ? JOB_OFFER_POINTS[offerTier] : 0;
  const work = workLengthPoints + highWage;
  const used = jobOffer > work ? 'jobOffer' : 'work';
  return { total: Math.max(work, jobOffer), workLength: workLengthPoints, highWage, jobOffer, used, workTier, offerTier };
}
